/**
 * ProfileCompletenessChecklist
 *
 * Dashboard banner shown to subscribed coaches whose profile is missing
 * one or more publish-required fields. Implements the "Frontend should
 * show a checklist" part of Subscription Gating Spec §5.2:
 *
 *   "Backend should reject POST /listings with incomplete_profile if
 *    any of the three is missing. Frontend should show a checklist
 *    on the coach dashboard ('Complete your profile to publish') with
 *    the missing items called out."
 *
 * Visibility rules:
 *   - Hidden when currentUser is missing / not a coach (provider)
 *   - Hidden when tier < 1 (the "Subscribe to be visible" banner
 *     handles tier 0; this one is the next step after subscribing)
 *   - Hidden when all three fields are present (profile photo, bio,
 *     expertise tag — expertise check is deferred until the field is
 *     standardized in the profile schema)
 *
 * Visual:
 *   Amber-toned inline banner (warmer than the gold-on-dark
 *   subscribe banner so the two are visually distinct when both
 *   could plausibly co-exist for a small window). Lists missing
 *   items with checkmark / bullet glyphs. Single CTA → profile
 *   settings page.
 *
 * Mounted in MainDashboardWrapper next to the other top-level banners.
 */

import React from 'react';
import { withRouter } from 'react-router-dom';

const ProfileCompletenessChecklist = ({ currentUser, history, location }) => {
  if (!currentUser || !currentUser.attributes) return null;

  const profile = currentUser.attributes.profile || {};
  const publicData = profile.publicData || {};
  const metadata = profile.metadata || {};
  const sd = metadata.subscriptionDetails || null;
  const tier = sd && typeof sd.subscription === 'number' ? sd.subscription : 0;

  // Only target subscribed coaches. Tier 0 sees the "Subscribe to be
  // visible" banner instead, which is the bigger gate.
  if (tier < 1) return null;
  if (!publicData.isProvider) return null;

  // Hide on profile settings itself (they're already in the right
  // place) and on the pricing page (they're solving a different problem
  // there).
  const path = (location && location.pathname) || '';
  if (path === '/profile-settings') return null;
  if (path.startsWith('/account/payments')) return null;

  // Determine which fields are missing. Mirrors the FE check in
  // Gallery.getPublishReadiness() and the BE check in listing-service
  // precheckListingCreate({mode: 'publish'}). When these three move,
  // keep all three call sites in sync.
  const missing = [];
  if (!profile.bio || !profile.bio.toString().trim().length) {
    missing.push({ key: 'bio', label: 'Add a coaching bio' });
  }
  // Sharetribe stores the profile image as a relationship. The
  // currentUser object from the user.duck has it included on
  // `profileImage` — check for that first, with fallbacks for the
  // various ways the FE may carry it.
  const hasPhoto = !!(
    (currentUser.profileImage && currentUser.profileImage.id) ||
    profile.profileImageId ||
    (profile.image && profile.image.id)
  );
  if (!hasPhoto) {
    missing.push({ key: 'photo', label: 'Upload a profile photo' });
  }
  // Expertise tag deferred — see Gallery.js / precheckListingCreate
  // comments. When the schema adds an expertise field, add the check
  // here.

  if (missing.length === 0) return null;

  const handleCTA = () => {
    if (history) history.push('/profile-settings');
  };

  return (
    <div
      role="region"
      aria-label="Complete your profile to publish"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        padding: '14px 18px',
        margin: '0 0 12px 0',
        borderRadius: '12px',
        background:
          'linear-gradient(90deg, rgba(245, 158, 11, 0.14) 0%, rgba(245, 158, 11, 0.05) 100%)',
        border: '1px solid rgba(245, 158, 11, 0.55)',
        color: '#451A03',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
          flexWrap: 'wrap',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span aria-hidden="true" style={{ fontSize: '18px' }}>📝</span>
          <strong
            style={{
              fontSize: '15px',
              fontWeight: 700,
              color: '#451A03',
            }}
          >
            Complete your profile to publish a listing
          </strong>
        </div>
        <button
          type="button"
          onClick={handleCTA}
          style={{
            appearance: 'none',
            cursor: 'pointer',
            padding: '8px 14px',
            borderRadius: '8px',
            border: 'none',
            background: '#F59E0B',
            color: '#FFFFFF',
            fontSize: '13px',
            fontWeight: 700,
            whiteSpace: 'nowrap',
          }}
        >
          Complete profile
        </button>
      </div>

      <ul
        style={{
          listStyle: 'none',
          padding: 0,
          margin: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: '4px',
        }}
      >
        {missing.map((item) => (
          <li
            key={item.key}
            style={{
              fontSize: '13px',
              lineHeight: 1.45,
              color: '#7C2D12',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <span aria-hidden="true" style={{ color: '#F59E0B', fontSize: '14px', lineHeight: 1 }}>
              ○
            </span>
            {item.label}
          </li>
        ))}
      </ul>
    </div>
  );
};

export default withRouter(ProfileCompletenessChecklist);
